import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { rootUrl } from '../../config/config';

const Field = ({ label, id, value, onChange, type = 'text', disabled = false }) => (
    <div>
        <label htmlFor={id} className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
        <input
            id={id}
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
            className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 ${disabled ? 'bg-gray-50 text-gray-400' : 'bg-white'}`}
        />
    </div>
);

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    return (
        <button
            onClick={copy}
            className="ml-2 text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700 hover:bg-orange-200 transition font-medium"
        >
            {copied ? 'Copied!' : 'Copy'}
        </button>
    );
};

/**
 * Modal for creating a portal login account for a driver or owner.
 *
 * Props:
 *   isOpen       - boolean
 *   onClose      - () => void
 *   onSuccess    - (result) => void — called after successful creation
 *   actorType    - 'driver' | 'owner'
 *   entityId     - driver id (for drivers) | undefined
 *   entityCode   - owner code (for owners) | undefined
 *   prefill      - { name, email } from entity record
 *   token        - auth token string
 */
export default function CreatePortalAccountModal({ isOpen, onClose, onSuccess, actorType, entityId, entityCode, prefill = {}, token }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [created, setCreated] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setName(prefill.name || '');
            setEmail(prefill.email || '');
            setUsername('');
            setPassword('');
            setError('');
            setCreated(null);
        }
    }, [isOpen, prefill.name, prefill.email]);

    if (!isOpen) return null;

    const endpoint = actorType === 'driver'
        ? `${rootUrl}/drivers/${entityId}/create-portal-account`
        : `${rootUrl}/owners/${entityCode}/create-portal-account`;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) { setError('Email is required'); return; }
        setLoading(true);
        setError('');
        try {
            const payload = { name, email };
            if (username.trim()) payload.username = username.trim();
            if (password.trim()) payload.password = password.trim();

            const res = await axios.post(endpoint, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCreated(res.data);
            if (typeof onSuccess === 'function') onSuccess(res.data);
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || err.message;
            setError(msg || 'Failed to create account');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h2 className="text-base font-semibold text-gray-800">Create Portal Account</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
                </div>

                <div className="px-6 py-5">
                    {created ? (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl p-3">
                                <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="text-sm text-green-700 font-medium">Account created. Credentials emailed to {email || prefill.email}.</p>
                            </div>

                            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3">
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Username</p>
                                    <div className="flex items-center">
                                        <span className="text-sm font-mono font-semibold text-gray-800">{created.username}</span>
                                        <CopyButton text={created.username} />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Password</p>
                                    <div className="flex items-center">
                                        <span className="text-sm font-mono font-semibold text-gray-800">{created.plainPassword}</span>
                                        <CopyButton text={created.plainPassword} />
                                    </div>
                                </div>
                            </div>

                            <p className="text-xs text-gray-400">Save these credentials. The password will not be shown again.</p>

                            <button
                                onClick={onClose}
                                className="w-full bg-orange-500 text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-orange-600 transition"
                            >
                                Done
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <p className="text-xs text-gray-500">Fields are pre-filled from the entity record. Edit if needed before creating.</p>

                            <Field label="Name" id="pa-name" value={name} onChange={setName} />
                            <Field label="Email *" id="pa-email" value={email} onChange={setEmail} type="email" />
                            <Field label="Username (leave blank to auto-generate)" id="pa-username" value={username} onChange={setUsername} />
                            <Field label="Password (leave blank to auto-generate)" id="pa-password" value={password} onChange={setPassword} type="text" />

                            {error && (
                                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
                            )}

                            <div className="flex gap-3 pt-1">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 bg-orange-500 text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-orange-600 transition disabled:opacity-60"
                                >
                                    {loading ? 'Creating...' : 'Create Account'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
